/**
 * What counts as a bookable slot on the schedule page.
 *
 * The date strip (`UI/DateStrip.tsx`, fed by `UI/DateSelector.tsx`) and the
 * per-provider slot list (`UI/ProviderList.tsx`) must agree on one
 * earliest-bookable floor. When they disagree, the strip enables a day that
 * the slot list then filters to empty and the patient hits a dead end — that
 * is the PLA-2779 bug. Both paths now take their floor from
 * {@link getEarliestBookableTime}, computed once in `UI/ProviderScheduler.tsx`
 * and threaded down.
 */

import type { TimeSlot } from "@frontend/lib/actions/actions";
import { addDays, addMinutes } from "date-fns";
import {
  DEFAULT_BOOKING_LEAD_TIME_DAYS,
  MINIMUM_BOOKING_BUFFER_MINUTES,
  getStartOfTodayInTimezone,
} from "./utils";

/**
 * The earliest moment a slot may start and still be bookable: the lead-time
 * day floor in the practice timezone, clamped forward to now + the checkout
 * buffer.
 */
export function getEarliestBookableTime(
  timeZone: string,
  leadTimeDays: number = DEFAULT_BOOKING_LEAD_TIME_DAYS,
): Date {
  const earliestBookableDay = addDays(
    getStartOfTodayInTimezone(timeZone),
    leadTimeDays,
  );
  // The day cutoff alone would still surface slots that are technically
  // "today" (or later) but too close to now to safely check out —
  // clamp to now + a buffer so a slot can't go stale mid-checkout.
  const earliestBookableNow = addMinutes(
    new Date(),
    MINIMUM_BOOKING_BUFFER_MINUTES,
  );
  return earliestBookableDay > earliestBookableNow
    ? earliestBookableDay
    : earliestBookableNow;
}

/** Drops every slot starting before `earliestBookableTime`. */
export function filterBookableSlots(
  slots: TimeSlot[],
  earliestBookableTime: Date,
): TimeSlot[] {
  return slots.filter(
    (slot) => new Date(slot.dateTime) >= earliestBookableTime,
  );
}

/**
 * Buckets bookable slots belonging to `availableProviderIds` by day, returning
 * a count per day key.
 *
 * The day key format is load-bearing: `DateStrip` looks days up with
 * `startOfDay(date).toString()`, so keys must stay this exact browser-local
 * midnight `Date.prototype.toString()` form. (That the key is browser-local
 * rather than practice-timezone is tracked separately — changing it here
 * without changing `DateStrip` would silently disable every day.)
 */
export function countBookableSlotsPerDay(
  slots: TimeSlot[],
  availableProviderIds: number[],
  earliestBookableTime: Date,
): Record<string, number> {
  const slotsPerDay: Record<string, number> = {};

  for (const slot of filterBookableSlots(slots, earliestBookableTime)) {
    if (!availableProviderIds.includes(slot.providerId)) {
      continue;
    }

    const date = new Date(slot.dateTime);
    date.setHours(0, 0, 0, 0);
    const dateKey = date.toString();

    slotsPerDay[dateKey] = (slotsPerDay[dateKey] ?? 0) + 1;
  }

  return slotsPerDay;
}
